import type { FilterDefinition, FilterValue, ListEmptyKind, MonthRangeValue } from "@/ds";
import { monthEnd, monthStart, validatePeriodo } from "@/modules/carga-trabalho";
import type { CargaTrabalhoPeriodo } from "@/modules/carga-trabalho";
import type { CargaChip } from "./components/carga-subbar";
import type { EmptyStateContent } from "./types-carga-de-trabalho";

const pad = (month: number) => String(month).padStart(2, "0");
const yyyyMm = ({ year, month }: { year: number; month: number }) => `${year}-${pad(month)}`;

/** Período padrão: o ano corrente, de janeiro a dezembro. */
export function periodoDoAno(now: Date = new Date()): MonthRangeValue {
  const year = now.getFullYear();
  return { from: { year, month: 1 }, to: { year, month: 12 } };
}

/** Primeiro dia do mês inicial e último do final, no formato do contrato (`YYYY-MM-DD`). */
export function datasDoPeriodo(range: MonthRangeValue): CargaTrabalhoPeriodo {
  return { inicio: monthStart(yyyyMm(range.from)), fim: monthEnd(yyyyMm(range.to)) };
}

/** Mensagem de erro do intervalo (fim antes do início, mais de 36 meses); `null` quando válido. */
export function erroDoPeriodo(range: MonthRangeValue): string | null {
  return validatePeriodo(yyyyMm(range.from), yyyyMm(range.to));
}

/** Um chip por filtro aplicado, com os rótulos das opções escolhidas; `rotulosVistos` cobre a opção que saiu da lista. */
export function chipsDosFiltros(
  columns: readonly FilterDefinition[],
  value: FilterValue,
  testIdPrefix: string,
  rotulosVistos: ReadonlyMap<string, string> = new Map(),
): CargaChip[] {
  return columns
    .filter(({ key }) => (value[key] ?? []).length > 0)
    .map((column) => ({
      key: column.key,
      label: column.label,
      testId: `${testIdPrefix}-${column.key}`,
      values: (value[column.key] ?? [])
        .map(
          (item) =>
            column.options?.find((option) => option.value === item)?.label ??
            rotulosVistos.get(item) ??
            item,
        )
        .join(", "),
    }));
}

/** Texto do vazio: o da busca, o dos filtros ou o de "sem alocação", conforme o que a pessoa aplicou. */
export function conteudoDoVazio(
  kind: ListEmptyKind,
  conteudos: { vazio: EmptyStateContent; busca: EmptyStateContent; filtro: EmptyStateContent },
): EmptyStateContent {
  if (kind === "sem-resultados-busca") return conteudos.busca;
  return kind === "sem-resultados-filtros" ? conteudos.filtro : conteudos.vazio;
}

export const VAZIO_BUSCA: EmptyStateContent = {
  title: "Nenhum resultado corresponde à busca.",
  description: "Tente revisar o termo pesquisado",
};

export const VAZIO_FILTRO: EmptyStateContent = {
  title: "Nenhum resultado corresponde aos filtros selecionados.",
  description: "Tente ajustar ou limpar os filtros",
};
